/**
 * The tenant switcher at the top of the side nav (DESIGN.md "Navigation",
 * carbon-shell `.switcher` and `.pop`): a 32px navy avatar with Soft Green
 * initials, the tenant name at 600 over its domain in helper text (D29), and
 * an up-down icon. It opens a 320px layer-02 menu, the one surface with the
 * menu shadow, anchored under the switcher: a find field, then one row per
 * tenant (avatar, name, domain, and a check on the current one). Escape and
 * an outside click close it (headless UI Popover).
 *
 * The menu carries `data-shell` (Gray 100 in both themes) and `data-menu`.
 * With nothing to switch to (`switchable` false) it is a static block.
 */
import { useMemo, useState } from 'react'
import { Popover } from '@headlessui/react'
import clsx from 'clsx'
import { Avatar } from './Avatar'
import { IconCheck, IconSearch, IconUpDown } from './icons'
import { initialsOf } from './initials'
import { IuvoMark } from './IuvoMark'
import type { TenantSwitcherProps } from './types'

const ROW =
  'grid w-full grid-cols-[32px_minmax(0,1fr)_16px] items-center gap-3 border-0 border-b border-layer-selected px-4 py-2 ' +
  'min-h-14 text-left transition-colors duration-110 ease-carbon hover:bg-layer-hover'

export function TenantSwitcher({
  tenants,
  currentId,
  onSelect,
  isLoading = false,
  allTenantsLabel,
  switchable = true,
}: TenantSwitcherProps) {
  const [query, setQuery] = useState('')
  const current = currentId ? tenants.find(t => t.id === currentId) : undefined
  const name = isLoading
    ? 'Loading tenants'
    : current?.name ?? (allTenantsLabel && !currentId ? allTenantsLabel : tenants[0]?.name ?? 'No tenant')
  const sub = isLoading
    ? ''
    : current
      ? current.domain ?? ''
      : allTenantsLabel && !currentId
        ? `${tenants.length} tenant${tenants.length === 1 ? '' : 's'}`
        : tenants[0]?.domain ?? ''

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter(t => `${t.name} ${t.domain ?? ''}`.toLowerCase().includes(q))
  }, [query, tenants])

  const identity = (
    <>
      <Avatar initials={initialsOf(name)} tone="tenant" />
      <span className="min-w-0">
        <b className="block truncate text-body font-semibold leading-[1.29] text-text-primary">{name}</b>
        {sub ? <small className="block truncate text-helper tabular-nums text-text-helper">{sub}</small> : null}
      </span>
    </>
  )

  if (!switchable || isLoading) {
    return (
      <div className="grid grid-cols-[32px_minmax(0,1fr)] items-center gap-3 border-b border-line-subtle px-4 py-3">{identity}</div>
    )
  }

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            aria-label={`Switch tenant. Current: ${name}`}
            className={clsx(
              'grid w-full grid-cols-[32px_minmax(0,1fr)_16px] items-center gap-3 border-0 border-b border-line-subtle px-4 py-3 text-left',
              'transition-colors duration-110 ease-carbon hover:bg-layer-2',
              open && 'bg-layer-2',
            )}
          >
            {identity}
            <IconUpDown className="text-[color:var(--icon-secondary)]" />
          </Popover.Button>
          <Popover.Panel
            focus
            data-shell=""
            data-menu=""
            className="absolute left-0 top-full z-[70] w-80 max-w-[calc(100vw-16px)] bg-layer-2 text-text-primary shadow-menu"
          >
            <label className="flex h-10 items-center gap-3 border-b border-line-strong bg-field px-4 text-[color:var(--icon-secondary)] focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-[color:var(--focus)]">
              <IconSearch />
              <span className="sr-only">Find a tenant</span>
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Find a tenant"
                autoComplete="off"
                className="h-[38px] min-w-0 flex-1 border-0 bg-transparent text-body text-text-primary outline-none placeholder:text-text-placeholder focus-visible:outline-none"
              />
            </label>
            <ul aria-label="Tenants" className="m-0 max-h-[min(420px,calc(100vh-160px))] list-none overflow-y-auto p-0">
              {allTenantsLabel && !query ? (
                <li>
                  <button
                    type="button"
                    aria-current={!currentId || undefined}
                    onClick={() => {
                      onSelect(undefined)
                      close()
                    }}
                    className={clsx(ROW, !currentId && 'bg-layer-selected')}
                  >
                    <span aria-hidden="true" className="grid h-8 w-8 place-items-center bg-layer-selected">
                      <IuvoMark size={16} />
                    </span>
                    <span className="min-w-0">
                      <b className="block truncate text-body font-semibold leading-[1.29]">{allTenantsLabel}</b>
                      <small className="block text-helper tabular-nums text-text-helper">
                        {tenants.length} tenant{tenants.length === 1 ? '' : 's'}
                      </small>
                    </span>
                    <IconCheck className={clsx('text-text-primary', currentId && 'invisible')} />
                  </button>
                </li>
              ) : null}
              {filtered.map(t => {
                const selected = t.id === currentId
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      aria-current={selected || undefined}
                      onClick={() => {
                        onSelect(t.id)
                        close()
                      }}
                      className={clsx(ROW, selected && 'bg-layer-selected')}
                    >
                      <Avatar initials={initialsOf(t.name)} tone="tenant" />
                      <span className="min-w-0">
                        <b className="block truncate text-body font-semibold leading-[1.29]">{t.name}</b>
                        {t.domain ? <small className="block truncate text-helper text-text-helper">{t.domain}</small> : null}
                      </span>
                      <IconCheck className={clsx('text-text-primary', !selected && 'invisible')} />
                    </button>
                  </li>
                )
              })}
            </ul>
            {filtered.length === 0 ? (
              <p className="m-0 px-4 py-3 text-helper text-text-helper">No tenant matches that search.</p>
            ) : null}
          </Popover.Panel>
        </>
      )}
    </Popover>
  )
}

export default TenantSwitcher
